import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, Alert } from 'react-bootstrap';
import {
	ADMIN_GET_DFS_PICKS,
	ADMIN_DELETE_DFS_PICK
} from '../../store/constants';
import { SPORTS } from './riskConfig';
import { checkHealthScoreStatus } from '../utils/dashboardCalculations';
import PickForm from './PickForm';
import './style.css';

const MAX_SLOTS = 4;

const riskModifier = (status) => {
	if (!status) return '';
	if (status === 'HIGH RISK') return 'HIGH_RISK';
	if (status === 'POSITIVE') return 'OPTIMAL';
	return status;
};

function AdminDFSPicks(props) {
	const { dfs, dispatch } = props;
	// editing: { sportsID, slotOrder, existing: pick|null }
	const [ editing, setEditing ] = useState(null);

	useEffect(() => {
		dispatch({ type: ADMIN_GET_DFS_PICKS });
	}, [ dispatch ]);

	const picks = dfs?.picks || {};

	const handleDelete = (pick, sportsID) => {
		if (!window.confirm('Remove this pick?')) return;
		dispatch({ type: ADMIN_DELETE_DFS_PICK, payload: { pickId: pick.id, sportsID } });
	};

	const startEdit = (sportsID, slotOrder, existing) => {
		setEditing({ sportsID, slotOrder, existing });
	};

	return (
		<div className='admin-dfs'>
			<h1>DFS Picks</h1>
			<p className='admin-dfs__empty'>
				Up to {MAX_SLOTS} featured players per sport. Each slot can include an optional prop bet description.
			</p>

			{dfs?.error && <Alert variant='danger'>{dfs.error}</Alert>}
			{dfs?.message && <Alert variant='success'>{dfs.message}</Alert>}

			<div className='admin-dfs__grid'>
				{SPORTS.map((sport) => {
					const sportPicks = Array.isArray(picks[ sport.id ]) ? picks[ sport.id ] : [];
					const nextSlot = sportPicks.length + 1;
					const isEditingThisSport = editing?.sportsID === sport.id;

					return (
						<div key={sport.id} className='admin-dfs__tile' data-testid={`admin-dfs-tile-${sport.name}`}>
							<h3>{sport.label}</h3>

							{sportPicks.length === 0 && (
								<div className='admin-dfs__empty'>No active picks — add one below</div>
							)}

							{sportPicks.map((pick) => {
								const health = pick.gamingScore != null
									? checkHealthScoreStatus(pick.gamingScore, null, 'player')
									: null;
								const teamLabel = pick.team?.name || pick.team?.city || '';
								const injury = pick.injuryStatus && pick.injuryStatus !== 'null' ? pick.injuryStatus : '';
								const isEditingThisSlot = isEditingThisSport && editing?.slotOrder === pick.slotOrder;

								return (
									<div key={pick.id} className='admin-dfs__slot'>
										<div className='admin-dfs__slot-header'>
											<span className='admin-dfs__slot-label'>Slot {pick.slotOrder}</span>
											<div className='admin-dfs__actions'>
												<Button
													size='sm'
													variant='primary'
													onClick={() => startEdit(sport.id, pick.slotOrder, pick)}
												>
													Edit
												</Button>
												<Button
													size='sm'
													variant='outline-danger'
													onClick={() => handleDelete(pick, sport.id)}
													disabled={dfs?.saving}
												>
													Remove
												</Button>
											</div>
										</div>
										<div className='admin-dfs__pick-line'>
											{health && (
												<span className={`admin-dfs__risk-dot admin-dfs__risk-dot--${riskModifier(health.status)}`} />
											)}
											<strong>{pick.playerName}</strong>
											{teamLabel && <> — {teamLabel}</>}
											{pick.position && <> · {pick.position}</>}
										</div>
										{pick.propBet && (
											<div className='admin-dfs__prop-bet'>Prop: {pick.propBet}</div>
										)}
										{health && <div className='admin-dfs__pick-line'>{health.status}</div>}
										{injury && <div className='admin-dfs__pick-line'>Injury: {injury}</div>}

										{isEditingThisSlot && (
											<PickForm
												sportsID={sport.id}
												sportsName={sport.name}
												slotOrder={pick.slotOrder}
												existing={pick}
												onDone={() => setEditing(null)}
											/>
										)}
									</div>
								);
							})}

							{sportPicks.length < MAX_SLOTS && (
								<>
									<div className='admin-dfs__actions'>
										<Button
											size='sm'
											variant='success'
											onClick={() => startEdit(sport.id, nextSlot, null)}
										>
											+ Add Player {nextSlot}
										</Button>
									</div>

									{isEditingThisSport && editing?.existing === null && (
										<PickForm
											sportsID={sport.id}
											sportsName={sport.name}
											slotOrder={nextSlot}
											existing={null}
											onDone={() => setEditing(null)}
										/>
									)}
								</>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

const mapStateToProps = (state) => ({
	user: state.user,
	dfs: state.adminDfsPicks
});

export default connect(mapStateToProps)(AdminDFSPicks);
